import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";
import { LegendList, LegendItem, ColorDot, ReactTooltipStyled } from "./styles";

const capitalize = (w) => w.charAt(0).toUpperCase() + w.slice(1);

// Game mode keys are inconsistent: snake_case (inverse_categories), camelCase (colorMixing)
// or PascalCase (Standard) for compound names. Try the variants and use the first that exists.
const keyVariants = (gameMode) => {
  const words = gameMode
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2") // split camelCase boundaries
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.toLowerCase());
  const lower = gameMode.toLowerCase();
  const variants = [
    gameMode, // verbatim (colorMixing, Standard)
    lower, // colormixing
    words.join("_"), // color_mixing / inverse_categories
    words.map((w, i) => (i === 0 ? w : capitalize(w))).join(""), // colorMixing
    words.map(capitalize).join(""), // ColorMixing
    capitalize(lower), // Standard
  ];
  return [...new Set(variants)];
};

// HTML legend for difficulty-modulator game modes: localized names (_name) + hover tooltip (_info).
// Replaces the native canvas legend (use with <Graphic forceHideLegend legend={<GameModeLegend .../>} />).
const GameModeLegend = ({ datasets = [], protocolPrefix, tooltipId = "gameModeLegendTip" }) => {
  const [t] = useTranslation("global");

  const lookupGameMode = (gameMode, suffix) => {
    if (!protocolPrefix || !gameMode) return "";
    for (const variant of keyVariants(gameMode)) {
      const text = t(`${protocolPrefix}_${variant}_${suffix}`, { defaultValue: "" });
      if (text) return text;
    }
    return "";
  };

  const getGameModeInfo = (gameMode) => lookupGameMode(gameMode, "info") || null;
  const getGameModeName = (gameMode) => lookupGameMode(gameMode, "name") || gameMode;

  // A game mode can appear in several datasets (sessionGraphics) -> show each label once
  const seen = new Set();
  const uniqueDatasets = datasets.filter((dataset) => {
    if (seen.has(dataset.label)) return false;
    seen.add(dataset.label);
    return true;
  });

  // Rebuild the tooltip bindings whenever the legend content changes
  const legendLabels = uniqueDatasets.map((dataset) => dataset.label).join(",");
  useEffect(() => {
    ReactTooltip.rebuild();
  }, [legendLabels, protocolPrefix]);

  return (
    <>
      <LegendList>
        {uniqueDatasets.map((dataset) => {
          const info = getGameModeInfo(dataset.label);
          const tooltipProps = info ? { "data-for": tooltipId, "data-tip": info } : {};
          return (
            <LegendItem key={dataset.label} $hasInfo={!!info} {...tooltipProps}>
              <ColorDot $color={Array.isArray(dataset.borderColor) ? dataset.borderColor[0] : dataset.borderColor} />
              {getGameModeName(dataset.label)}
            </LegendItem>
          );
        })}
      </LegendList>
      <ReactTooltipStyled id={tooltipId} place="top" type="dark" effect="solid" multiline delayShow={100} delayHide={100} />
    </>
  );
};

export default GameModeLegend;
